import { useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { m as motion } from 'framer-motion';
import { Eye, EyeOff, Mail, ShieldCheck, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { toast } from '@/stores/ui';
import { authService, emailOtpService, DEMO_OTP_CODE, type SessionUser } from '@/services/auth';
import { authEventsService } from '@/services/authEvents';
import { mfaService } from '@/services/mfa';
import { useSession } from '@/stores/session';
import { inviteRegisterSchema, loginSchema, registerSchema, type RegisterInput } from '@/lib/schemas/auth';
import { setRememberMe } from '@/lib/supabase';
import { CaptchaGate } from '@/components/system/CaptchaGate';
import { cn } from '@/lib/cn';

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.4a5.5 5.5 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.6-5.2 3.6-8.8z"/>
    <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3a7.2 7.2 0 0 1-10.8-3.8H1.2v3.1A12 12 0 0 0 12 24z"/>
    <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.2a12 12 0 0 0 0 10.8l4.1-3.1z"/>
    <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8L20 3.2A12 12 0 0 0 1.2 6.6l4.1 3.1A7.2 7.2 0 0 1 12 4.8z"/>
  </svg>
);
const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M16.7 12.9c0-2.4 2-3.6 2-3.7-1.1-1.6-2.8-1.8-3.4-1.8-1.5-.2-2.8.9-3.6.9-.7 0-1.9-.8-3.1-.8-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.5.8 1.2 1.7 2.4 3 2.4 1.2-.1 1.6-.8 3.1-.8s1.8.8 3.1.7c1.3 0 2.1-1.1 2.9-2.3.9-1.4 1.3-2.7 1.3-2.7s-2.5-1-2.6-3.8zM14.4 5.2c.7-.8 1.1-1.9 1-3.2-1 .1-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.5 2.9-1.3z"/>
  </svg>
);
const MicrosoftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
    <path fill="#F25022" d="M2 2h9.5v9.5H2z"/>
    <path fill="#7FBA00" d="M12.5 2H22v9.5h-9.5z"/>
    <path fill="#00A4EF" d="M2 12.5h9.5V22H2z"/>
    <path fill="#FFB900" d="M12.5 12.5H22V22h-9.5z"/>
  </svg>
);

/** Fondo del login (VLT-390): video premium servido desde R2 vía el Worker.
 * Puramente decorativo: silenciado, en bucle, sin controles y oculto para
 * lectores de pantalla. El overlay garantiza el contraste del formulario;
 * prefers-reduced-motion lo retira; si el video no carga (demo local, R2
 * caído) queda el fondo del tema — degradación silenciosa. */
function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="relative grid min-h-dvh place-items-center overflow-hidden px-4 py-10">
      <video
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-40 motion-reduce:hidden"
        src="/media/login-bg.mp4"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        tabIndex={-1}
      />
      {/* Feedback del propietario 5-sep: el video se comía el texto del logo
          y los letreros AI del fondo distraían — overlay más firme. */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/65 via-background/45 to-background/80" aria-hidden="true" />
      <div className="relative w-full max-w-sm">{children}</div>
    </div>
  );
}

/** Login/Registro (VLT-018/019/249) — social primero, email como alternativa, MFA en Fase B. */

// Páginas legales del sitio público (aviso legal, términos y privacidad).
const SITE_URL = 'https://vaultex-site.cassimmit.com';
const AUTOCOMPLETE: Partial<Record<string, string>> = {
  email: 'email', password: 'password', fullName: 'name', orgName: 'organization',
};
export function AuthPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // VLT-405: llegada desde un correo de invitación — registro sin nombre de
  // firma (el trigger 0023 une el perfil a la org invitante). `invite=1` es
  // de EQUIPO; `invite=client` (VLT-409) es un cliente invitado a su portal:
  // mismo registro, otro aviso — a un cliente no se le habla de «equipo».
  const [searchParams] = useSearchParams();
  const inviteKind = searchParams.get('invite');
  // VLT-064: la sesión se cerró sola — se dice por qué al volver al login.
  const closedReason = searchParams.get('reason');
  const invited = inviteKind === '1' || inviteKind === 'client';
  const [mode, setMode] = useState<'login' | 'register'>(invited ? 'register' : 'login');
  const [showPassword, setShowPassword] = useState(false);
  // Desafío MFA tras la contraseña (VLT-307): usuario en espera + código TOTP.
  const [mfaPending, setMfaPending] = useState<SessionUser | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);
  // OTP de login por correo (12-sep): para cuentas SIN MFA por app. La casilla
  // «Recordar este equipo» evita el código en los próximos logins (30 días).
  const [emailOtpPending, setEmailOtpPending] = useState<SessionUser | null>(null);
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailOtpBusy, setEmailOtpBusy] = useState(false);
  const [emailOtpRemember, setEmailOtpRemember] = useState(true);
  // Restablecimiento (VLT-335/065): pedir el enlace desde el login, con
  // respuesta NEUTRA (sin revelar si la cuenta existe).
  const [rememberMe, setRememberMeState] = useState(true);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  // VLT-018: token de la verificación de seguridad (solo si el widget está activo).
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [forgotSent, setForgotSent] = useState(false);
  // VLT-069: cuenta en pausa por intentos fallidos — aviso junto al botón,
  // con el restablecimiento a mano (el correo desbloquea).
  const [lockedMsg, setLockedMsg] = useState<string | null>(null);
  // Cuenta creada pendiente de confirmar el correo: pantalla con reenvío.
  const [confirmPending, setConfirmPending] = useState<string | null>(null);
  const isLogin = mode === 'login';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<RegisterInput>({
    resolver: zodResolver(isLogin ? (loginSchema as never) : invited ? (inviteRegisterSchema as never) : registerSchema),
    defaultValues: { accountType: 'firm' },
  });
  const accountType = watch('accountType') ?? 'firm';

  const social = async (provider: 'google' | 'apple' | 'azure') => {
    const name = provider === 'google' ? 'Google' : provider === 'apple' ? 'Apple' : 'Microsoft';
    if (!authService.isReal) {
      toast(t('auth.socialDemo', { provider: name }), 'info');
      navigate('/');
      return;
    }
    const res = await authService.signInWithOAuth(provider);
    if (!res.ok) toast(t(res.errorKey), 'info');
    // Con éxito el navegador redirige al proveedor; no navegamos aquí.
  };

  const onSubmit = handleSubmit(async (values) => {
    // El flag va ANTES del login: el adaptador de storage decide con él
    // dónde persiste la sesión (localStorage vs sessionStorage).
    if (isLogin) setRememberMe(rememberMe);
    const res = isLogin
      ? await authService.signInWithPassword(values.email, values.password, captchaToken ?? undefined)
      : await authService.signUp(values, captchaToken ?? undefined);
    if (!res.ok) {
      if (res.errorKey === 'auth.errors.locked' || res.errorKey === 'auth.errors.lockedLater') {
        setLockedMsg(t(res.errorKey, { count: res.minutes ?? 0 }));
        setForgotOpen(true);
        setForgotSent(false);
        setForgotEmail(values.email);
        return;
      }
      toast(t(res.errorKey), 'info');
      return;
    }
    setLockedMsg(null);
    if (res.needsEmailConfirm) {
      setConfirmPending(values.email);
      return;
    }
    if (res.needsMfa) {
      setMfaPending(res.user);
      return;
    }
    // Sin MFA por app, el login pide el código enviado al correo. 'skip'
    // (equipo recordado) y 'unavailable' (correo del Worker sin configurar)
    // siguen derecho — el paso jamás bloquea por infraestructura.
    if (isLogin) {
      const otp = await emailOtpService.begin(i18n.language.startsWith('en') ? 'en' : 'es');
      if (otp === 'sent') {
        setEmailOtpPending(res.user);
        return;
      }
      // Sin poder enviar el código: si el servidor lo exige (0077), no se
      // entra a una app vacía; se avisa y se cierra la sesión.
      if (otp === 'unavailable') {
        const st = await emailOtpService.status();
        if (st?.required && !st.ok) {
          await authService.signOut({ silent: true });
          toast(t('auth.otp.unavailable'), 'info');
          return;
        }
      }
    }
    if (res.user) useSession.getState().setUser(res.user);
    toast(t(isLogin ? 'auth.welcomeBack' : 'auth.accountCreated'));
    navigate('/');
  });

  const verifyMfa = async (code = mfaCode) => {
    setMfaBusy(true);
    const ok = await mfaService.verifyChallenge(code.trim());
    setMfaBusy(false);
    if (!ok) {
      toast(t('mfa.badCode'), 'info');
      setMfaCode(''); // listo para teclear el siguiente sin borrar a mano
      return;
    }
    void authEventsService.record('mfa_challenge');
    if (mfaPending) useSession.getState().setUser(mfaPending);
    toast(t('auth.welcomeBack'));
    navigate('/');
  };

  const verifyEmailOtp = async (code = emailOtpCode) => {
    setEmailOtpBusy(true);
    const ok = await emailOtpService.verify(code, emailOtpRemember);
    setEmailOtpBusy(false);
    if (!ok) {
      toast(t('auth.otp.badCode'), 'info');
      setEmailOtpCode('');
      return;
    }
    void authEventsService.record('mfa_challenge');
    if (emailOtpPending) useSession.getState().setUser(emailOtpPending);
    toast(t('auth.welcomeBack'));
    navigate('/');
  };

  const switchMode = (m: 'login' | 'register') => {
    setMode(m);
    reset();
  };

  const field = (name: keyof RegisterInput, label: string, placeholder: string, type = 'text') => {
    const isPassword = type === 'password';
    return (
      <label className="block">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <div className="relative">
          <input
            type={isPassword && showPassword ? 'text' : type}
            placeholder={placeholder}
            // 2-nov (web-interface-guidelines): el navegador y el gestor de
            // contraseñas saben qué ofrecer en cada campo.
            autoComplete={AUTOCOMPLETE[name] === 'password' ? (isLogin ? 'current-password' : 'new-password') : AUTOCOMPLETE[name]}
            spellCheck={type === 'email' || isPassword ? false : undefined}
            {...register(name)}
            aria-invalid={!!errors[name]}
            className={cn(
              'focus-ring mt-1.5 w-full rounded-xl border bg-muted/30 px-3.5 py-2.5 text-sm outline-none placeholder:text-muted-foreground',
              isPassword && 'pr-11',
              errors[name] ? 'border-danger/60' : 'border-border/60',
            )}
          />
          {/* El ojo: ver/ocultar la contraseña (petición del propietario 3-sep) */}
          {isPassword && (
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={t(showPassword ? 'auth.hidePassword' : 'auth.showPassword')}
              aria-pressed={showPassword}
              className="focus-ring absolute right-2 top-1/2 mt-0.5 -translate-y-1/2 rounded-lg p-1.5 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
        </div>
        {errors[name] && (
          <span role="alert" className="mt-1 block text-xs text-danger">{t(errors[name]?.message ?? 'forms.errors.generic')}</span>
        )}
      </label>
    );
  };

  // Cuenta creada, correo por confirmar (VLT-335): estado propio con reenvío
  // en vez de un toast que desaparece.
  if (confirmPending) {
    return (
      <AuthShell>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="glass rounded-2xl p-6 text-center">
            <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-brand-gradient text-white shadow-glow"><ShieldCheck className="h-6 w-6" /></span>
            <h1 className="font-display text-lg font-bold">{t('auth.confirmTitle')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('auth.confirmHint', { email: confirmPending })}</p>
            <Button
              className="mt-4 w-full"
              onClick={() => {
                void authService.resendVerification(confirmPending).then(() => {
                  toast(t('auth.confirmResent'), 'info');
                });
              }}
            >
              {t('auth.confirmResend')}
            </Button>
            <button type="button" onClick={() => { setConfirmPending(null); setMode('login'); }}
              className="focus-ring mt-3 text-xs font-medium text-muted-foreground hover:text-foreground">
              {t('portal.back')}
            </button>
          </div>
        </motion.div>
      </AuthShell>
    );
  }

  // Paso intermedio del OTP por correo (12-sep): pantalla para ENTRAR el
  // código enviado, con «Recordar este equipo» para no pedirlo de nuevo.
  if (emailOtpPending) {
    return (
      <AuthShell>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="glass rounded-2xl p-6 text-center">
            <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-brand-gradient text-white shadow-glow"><Mail className="h-6 w-6" /></span>
            <h1 className="font-display text-lg font-bold">{t('auth.otp.title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('auth.otp.hint')}</p>
            {!authService.isReal && (
              <p className="mt-2 text-xs text-muted-foreground">{t('auth.otp.demoHint', { code: DEMO_OTP_CODE })}</p>
            )}
            <form
              className="mt-4 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                void verifyEmailOtp();
              }}
            >
              <input
                value={emailOtpCode}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setEmailOtpCode(v);
                  // Mismo gesto que el MFA: auto-verificar al sexto dígito.
                  if (v.length === 6 && !emailOtpBusy) void verifyEmailOtp(v);
                }}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                placeholder="123456"
                aria-label={t('auth.otp.codeLabel')}
                className="focus-ring mx-auto block w-44 rounded-xl border border-border/60 bg-muted/30 px-3.5 py-2.5 text-center font-mono text-lg tracking-[0.3em] outline-none placeholder:text-muted-foreground"
              />
              <label className="flex cursor-pointer items-center justify-center gap-2.5">
                <input type="checkbox" checked={emailOtpRemember} onChange={(e) => setEmailOtpRemember(e.target.checked)}
                  className="h-4 w-4 accent-primary" />
                <span className="text-xs text-muted-foreground">{t('auth.otp.remember')}</span>
              </label>
              <Button type="submit" className="w-full" loading={emailOtpBusy} disabled={emailOtpCode.length !== 6}>
                {t('auth.otp.cta')}
              </Button>
            </form>
            <div className="mt-3 flex items-center justify-center gap-4">
              <button type="button"
                onClick={() => {
                  void emailOtpService.begin(i18n.language.startsWith('en') ? 'en' : 'es').then(() => {
                    toast(t('auth.otp.resent'), 'info');
                  });
                }}
                className="focus-ring text-xs font-medium text-primary">
                {t('auth.otp.resend')}
              </button>
              <button type="button" onClick={() => { setEmailOtpPending(null); setEmailOtpCode(''); }}
                className="focus-ring text-xs font-medium text-muted-foreground hover:text-foreground">
                {t('portal.back')}
              </button>
            </div>
          </div>
        </motion.div>
      </AuthShell>
    );
  }

  // Paso intermedio del login con MFA activo (VLT-307): solo el código TOTP.
  if (mfaPending) {
    return (
      <AuthShell>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="glass rounded-2xl p-6 text-center">
            <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-brand-gradient text-white shadow-glow"><Smartphone className="h-6 w-6" /></span>
            <h1 className="font-display text-lg font-bold">{t('mfa.challengeTitle')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('mfa.challengeHint')}</p>
            <form
              className="mt-4 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                void verifyMfa();
              }}
            >
              <input
                value={mfaCode}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setMfaCode(v);
                  // Auto-verificar al sexto dígito (petición del propietario
                  // 4-sep): sin clic extra; el guard evita el doble envío.
                  if (v.length === 6 && !mfaBusy) void verifyMfa(v);
                }}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                placeholder="123456"
                aria-label={t('mfa.codeLabel')}
                className="focus-ring mx-auto block w-44 rounded-xl border border-border/60 bg-muted/30 px-3.5 py-2.5 text-center font-mono text-lg tracking-[0.3em] outline-none placeholder:text-muted-foreground"
              />
              <Button type="submit" className="w-full" loading={mfaBusy} disabled={mfaCode.length !== 6}>
                {t('mfa.challengeCta')}
              </Button>
            </form>
            <button type="button" onClick={() => { setMfaPending(null); setMfaCode(''); }}
              className="focus-ring mt-3 text-xs font-medium text-muted-foreground hover:text-foreground">
              {t('portal.back')}
            </button>
          </div>
        </motion.div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-brand-gradient text-white shadow-glow"><ShieldCheck className="h-6 w-6" /></span>
          <h1 className="font-display text-xl font-bold">Vaul<span className="text-gradient">Tex</span></h1>
          <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-foreground/80 [text-shadow:0_1px_8px_hsl(var(--background)/.9)]">{t('brand.tagline')}</p>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl bg-muted/30 p-1" role="tablist">
            {(['login', 'register'] as const).map((m) => (
              <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => switchMode(m)}
                className={cn('focus-ring rounded-lg py-2 text-sm font-semibold transition-colors', mode === m ? 'bg-card text-foreground shadow-glass' : 'text-muted-foreground')}>
                {t(`auth.${m}`)}
              </button>
            ))}
          </div>

          <form onSubmit={onSubmit} className="space-y-3.5" noValidate>
            {isLogin && (closedReason === 'idle' || closedReason === 'expired' || closedReason === 'otp') && (
              <p role="status" data-testid="session-closed"
                className="rounded-xl bg-primary/10 px-3 py-2.5 text-xs leading-snug text-foreground">
                {t(closedReason === 'idle' ? 'session.closedIdle' : closedReason === 'otp' ? 'session.closedOtp' : 'session.closedExpired')}
              </p>
            )}
            {/* Invitación (equipo o cliente): aviso claro y sin campos de firma */}
            {!isLogin && invited && (
              <p className="rounded-xl bg-primary/10 px-3 py-2.5 text-xs leading-snug text-foreground">
                {t(inviteKind === 'client' ? 'auth.invitedClientNotice' : 'auth.invitedNotice')}
              </p>
            )}
            {/* Cada cuenta es un cliente de VaulTex: empresa o preparador independiente */}
            {!isLogin && !invited && (
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t('auth.accountType')}>
                {(['firm', 'independent'] as const).map((k) => (
                  <button key={k} type="button" role="radio" aria-checked={accountType === k}
                    onClick={() => setValue('accountType', k)}
                    className={cn(
                      'focus-ring rounded-xl border px-3 py-2.5 text-left transition-colors',
                      accountType === k ? 'border-primary/60 bg-primary/10' : 'border-border/60 hover:border-primary/40',
                    )}>
                    <span className="block text-xs font-semibold">{t(`auth.accountTypes.${k}`)}</span>
                    <span className="block text-[10.5px] leading-snug text-muted-foreground">{t(`auth.accountTypes.${k}Hint`)}</span>
                  </button>
                ))}
              </div>
            )}
            {!isLogin && field('fullName', t('forms.fullName'), 'María Santos')}
            {!isLogin && !invited && field('orgName', accountType === 'independent' ? t('auth.orgNameIndependent') : t('auth.orgName'), 'Cassimmit Tax Group')}
            {field('email', t('forms.email'), 'nombre@empresa.com', 'email')}
            {field('password', t('auth.password'), '••••••••••', 'password')}
            {/* VLT-386: sin marcar, la sesión muere al cerrar el navegador */}
            {isLogin && (
              <label className="flex cursor-pointer items-center gap-2.5">
                <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMeState(e.target.checked)}
                  className="h-4 w-4 accent-primary" />
                <span className="text-xs text-muted-foreground">{t('auth.rememberMe')}</span>
              </label>
            )}
            {/* Estándar Cassimmit 61 a 63: casilla SIN marcar de antemano; la
                base registra versión, hora, IP y dispositivo (0079). */}
            {!isLogin && (
              <div>
                <label className="flex cursor-pointer items-start gap-2.5">
                  <input type="checkbox" {...register('acceptLegal')} data-testid="accept-legal"
                    aria-invalid={errors.acceptLegal ? true : undefined}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-primary" />
                  <span className="text-xs leading-relaxed text-muted-foreground">
                    {t('auth.acceptLegal')}{' '}
                    <a href={`${SITE_URL}/terms`} target="_blank" rel="noopener" className="underline hover:text-foreground">{t('auth.legalTerms')}</a>
                    {' '}{t('auth.legalAnd')}{' '}
                    <a href={`${SITE_URL}/privacy`} target="_blank" rel="noopener" className="underline hover:text-foreground">{t('auth.legalPrivacy')}</a>.
                  </span>
                </label>
                {errors.acceptLegal && (
                  <span role="alert" className="mt-1 block text-xs text-danger">{t(errors.acceptLegal.message ?? 'forms.errors.acceptLegal')}</span>
                )}
              </div>
            )}
            {/* Verificación de seguridad (VLT-018): solo aparece con la site
                key configurada; sin ella, nada cambia. */}
            <CaptchaGate onToken={setCaptchaToken} />
            {isLogin && lockedMsg && (
              <p role="alert" data-testid="login-locked"
                className="rounded-xl border border-gold/40 bg-gold/10 px-3 py-2.5 text-xs leading-relaxed">
                {lockedMsg}
              </p>
            )}
            <Button type="submit" className="w-full" loading={isSubmitting}>
              {t(isLogin ? 'auth.loginCta' : 'auth.registerCta')}
            </Button>
            {isLogin && (
              <p className="text-center text-[11px] leading-snug text-muted-foreground">
                {t('auth.legalNoteShort')}{' '}
                <a href={`${SITE_URL}/terms`} target="_blank" rel="noopener" className="underline hover:text-foreground">{t('auth.legalTerms')}</a>
                {' '}{t('auth.legalAnd')}{' '}
                <a href={`${SITE_URL}/privacy`} target="_blank" rel="noopener" className="underline hover:text-foreground">{t('auth.legalPrivacy')}</a>.
              </p>
            )}
          </form>

          {/* Social al final del formulario (petición del propietario 2026-07-24) */}
          <div className="my-4 flex items-center gap-3 text-[10px] uppercase tracking-widest text-muted-foreground">
            <span className="h-px flex-1 bg-border/70" />{t('auth.orSocial')}<span className="h-px flex-1 bg-border/70" />
          </div>

          {/* Los TRES círculos (pedido 12-sep): Google, Apple y Microsoft —
              registro fácil; el perfil se completa después, poco a poco. */}
          <div className="flex items-center justify-center gap-4">
            {([
              ['google', 'Google', <GoogleIcon key="g" />],
              ['apple', 'Apple', <AppleIcon key="a" />],
              ['azure', 'Microsoft', <MicrosoftIcon key="m" />],
            ] as const).map(([provider, name, icon]) => (
              <button key={provider} type="button" onClick={() => void social(provider)}
                aria-label={t('auth.withProvider', { provider: name })}
                title={t('auth.withProvider', { provider: name })}
                className="focus-ring grid h-12 w-12 place-items-center rounded-full border border-border/60 bg-card/50 transition-colors hover:border-primary/50 hover:bg-card/80">
                {icon}
              </button>
            ))}
          </div>

          {isLogin && !forgotOpen && (
            <button type="button" onClick={() => { setForgotOpen(true); setForgotSent(false); }}
              className="focus-ring mt-4 block w-full text-center text-xs font-medium text-primary">
              {t('auth.forgot')}
            </button>
          )}

          {/* VLT-335/065: pedir el enlace de restablecimiento. La respuesta es
              SIEMPRE la misma, exista o no la cuenta (sin enumeración). */}
          {isLogin && forgotOpen && (
            <div className="mt-4 rounded-xl border border-border/60 bg-muted/20 p-3">
              {forgotSent ? (
                <p role="status" className="text-xs leading-relaxed text-muted-foreground">{t('auth.forgotSent')}</p>
              ) : (
                <form
                  className="space-y-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const email = forgotEmail.trim();
                    if (!/^\S+@\S+\.\S+$/.test(email)) {
                      toast(t('forms.errors.email'), 'info');
                      return;
                    }
                    setForgotBusy(true);
                    void authService.requestPasswordReset(email).then(() => {
                      setForgotBusy(false);
                      setForgotSent(true);
                    });
                  }}
                >
                  <label className="block">
                    <span className="text-xs font-medium text-muted-foreground">{t('auth.forgotLabel')}</span>
                    <input
                      type="email"
                      autoComplete="email"
                      spellCheck={false}
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="nombre@empresa.com"
                      className="focus-ring mt-1.5 w-full rounded-xl border border-border/60 bg-muted/30 px-3.5 py-2.5 text-sm outline-none placeholder:text-muted-foreground"
                    />
                  </label>
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" className="flex-1" loading={forgotBusy}>
                      {t('auth.forgotCta')}
                    </Button>
                    <button type="button" onClick={() => setForgotOpen(false)}
                      className="focus-ring rounded-lg px-3 text-xs font-medium text-muted-foreground hover:text-foreground">
                      {t('portal.back')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
        <p className="mt-4 text-center text-[11px] text-muted-foreground">{t('auth.mfaNote')}</p>
      </motion.div>
    </AuthShell>
  );
}
